import { Button, Divider, Stack, useMediaQuery, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useFormikContext } from 'formik'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { ButtonWithLoading } from '@e3dc-react/shell/Elements/Buttons'

export interface FormProps<T> {
  /**
   * The initial form model of the parent Formik context
   */
  model: T

  /**
   * Changes the state of the submit-button
   */
  isLoading?: boolean

  /**
   * A list of (potential control) elements that can occupy the left side of the control bar
   */
  leftControls?: React.ReactNode

  /**
   * An optional displayed divider between the main content and the control bar
   */
  separator?: boolean

  /**
   * An optional divider color. Default is portal green.
   */
  separatorColor?: string
  children?: React.ReactNode
}

/**
 * A simple Box that reuses the Formik context and provide a generic submit and reset button at the bottom of the Box.
 *
 * @param children Any React element that shall be displayed
 * @param model The initial form model. used to handle to reset the Formik-context processing states if it changes
 * @param isLoading A information if the submit-control shall switch into the loading state
 * @param leftControls Controls that can occupy otherwise unused space on the left side of the bottom control bar.
 * @param separator A optional displayed divider between the main content and the bottom control bar
 * @param separatorColor An optional separator color. Default is the portal green.
 * @constructor
 */
const FormBox: React.FC<FormProps<unknown>> = ({ children, model, isLoading, leftControls, separatorColor, separator = false }) => {
  const { resetForm, dirty, isValid, handleSubmit } = useFormikContext<unknown>()
  const { breakpoints } = useTheme()
  const submitIsDisabled = useMemo(() => {
    return !dirty || !isValid
  }, [dirty, isValid])
  const { t } = useCustomTranslation()

  useEffect(() => {
    resetForm()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model])

  const handleResetForm = useCallback(() => {
    resetForm()
  }, [resetForm])

  const fullWidthButtons = useMediaQuery(breakpoints.down('md'))

  return (
    <form onSubmit={handleSubmit}>
      {children}
      {separator && <Divider sx={{ mb: 2 }} style={{ borderColor: separatorColor }} />}
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent={'space-between'}>
        <Stack>{leftControls}</Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent={{ md: 'end' }} alignItems={{ xs: 'center' }} spacing={2}>
          <Button
            type="button"
            variant="outlined"
            size="large"
            color="inherit"
            disabled={!dirty}
            onClick={handleResetForm}
            fullWidth={fullWidthButtons}
          >
            {t('common.reset')}
          </Button>
          <ButtonWithLoading
            fullWidth={fullWidthButtons}
            size="large"
            color="primary"
            disabled={submitIsDisabled}
            isLoading={isLoading}
            onClick={handleSubmit}
            label={t('common.save')}
          />
        </Stack>
      </Stack>
    </form>
  )
}

export default FormBox
